import type { PAGE_POSITIONS } from 'types';
import type { DirectProductListItem } from 'types/capi/plp';

import AffiliateProductLink from '~/components/AffiliateProductLink';
import Price from '~/components/Price';
import StockStatus from '~/components/StockStatus';
import type { TrackedButtonClickName } from '~/services/gtm.client';

type Props = {
  cardSize: 'small' | 'large';
  className?: string;
  imageLoading?: 'lazy' | 'eager';
  name?: TrackedButtonClickName;
  onClick?: () => void;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  product: DirectProductListItem;
};

const DirectProductCard = ({
  pagePosition,
  product,
  padImage,
  cardSize,
  className,
  imageLoading
}: Props) => {
  return (
    <div className={`relative ${className}`}>
      <AffiliateProductLink
        urlTemplates={product.cta}
        affiliate={product.affiliate}
        price={product.price}
        product={product}
        buttonPosition={0}
        pagePosition={pagePosition}
        variant="none"
      >
        <div className="flex cursor-pointer flex-col justify-center gap-2 hover:opacity-80">
          <div className="absolute left-2 top-2 lg:left-4 lg:top-4">
            <StockStatus
              status="in-stock"
              releaseDateFormat={cardSize === 'large' ? 'long' : 'short'}
            />
          </div>
          <ProductCardImage
            alt={product.image?.alt}
            url={product.image?.url}
            padImage={padImage}
            cardSize={cardSize}
            imageLoading={imageLoading}
          />
        </div>

        <div className="mt-1 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h4
              data-cy="product-name"
              className="line-clamp-1 text-left text-sm font-semibold"
            >
              {product.name}
            </h4>
            <Price
              regular={product.price.regular}
              sale={product.price.sale}
              currency={product.price.currency}
              className="gap-2 text-sm"
            />
          </div>
          <BuyNow
            product={product}
            buttonPosition={0}
            pagePosition="PLP Direct Carousel"
          />
        </div>
      </AffiliateProductLink>
    </div>
  );
};

type ProductCardImageProps = {
  alt?: string;
  cardSize: 'small' | 'large';
  imageLoading: Props['imageLoading'];
  padImage: boolean;
  url?: string;
};

const ProductCardImage = ({ alt, url }: ProductCardImageProps) => {
  if (!url) return <SquareImageSkeleton />;

  return (
    <SquareImageSkeleton>
      <div className="size-full">
        <img
          className="aspect-square h-full w-auto object-cover"
          src={url}
          alt={alt}
        />
      </div>
    </SquareImageSkeleton>
  );
};

const SquareImageSkeleton = ({ children }: { children?: JSX.Element }) => {
  return (
    <div className="flex aspect-square w-full items-center justify-center bg-white">
      {children}
    </div>
  );
};

export const BuyNow = ({
  product,
  buttonPosition,
  pagePosition
}: {
  buttonPosition: number;
  pagePosition: PAGE_POSITIONS;
  product: DirectProductListItem;
}) => {
  return (
    <AffiliateProductLink
      urlTemplates={product.cta}
      affiliate={product.affiliate}
      price={product.price}
      product={product}
      buttonPosition={buttonPosition}
      pagePosition={pagePosition}
      variant="forest"
    >
      <div className="flex h-8 items-center justify-center gap-2">
        <div className="flex items-center gap-2 text-base">
          <div className="m-auto flex size-7 items-center rounded-md bg-white p-[0.05rem]">
            <img
              src={product.affiliate.logo}
              alt={product.affiliate.name}
              className="rounded bg-white object-cover text-black"
            />
          </div>
          <span>Buy from {product.affiliate.name}</span>
        </div>
      </div>
    </AffiliateProductLink>
  );
};

export default DirectProductCard;
